import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, onScopeDispose, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { fetchHostedSite, hostedSiteQueryKey, type TeamPid } from '../services/hostedApi'
import { rememberPageNames } from '../services/pageNames'

/**
 * The project's hosted site: pages, pending changes and the review digest.
 * The PublishBar, the Pages index and Settings share this one query, so a
 * change made on any of them updates the others.
 */
export function useHostedSite(teamPid: MaybeRefOrGetter<TeamPid | null>, enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: computed(() => hostedSiteQueryKey(toValue(teamPid))),
    queryFn: () => {
      const pid = toValue(teamPid)
      if (pid === null) {
        throw new Error('No project selected')
      }
      return fetchHostedSite(pid).then((site) => {
        rememberPageNames(site.pages)
        return site
      })
    },
    enabled: computed(() => toValue(teamPid) !== null && toValue(enabled))
  })
}

export function useInvalidateHostedSite() {
  const queryClient = useQueryClient()
  return (teamPid: TeamPid | null) => queryClient.invalidateQueries({ queryKey: hostedSiteQueryKey(teamPid) })
}

/**
 * Refreshes the site whenever a page or the site styles gets a new draft head, so the PublishBar counts dashboard,
 * Righty and MCP edits as they land. The server broadcasts `HostedDraftUpdated` to every tab, the editing one too.
 */
export function useHostedDraftRefresh(teamPid: MaybeRefOrGetter<TeamPid | null>) {
  const invalidate = useInvalidateHostedSite()
  let channel: EchoChannel | null = null
  const handler = () => {
    void invalidate(toValue(teamPid))
  }
  const stop = () => {
    // The channel object, not `Echo.private(name)`: the config store leaves the old channel on a team switch, and
    // naming it again would rejoin it.
    channel?.stopListening('.HostedDraftUpdated', handler)
    channel = null
  }
  watch(
    () => toValue(teamPid),
    (pid) => {
      stop()
      if (pid !== null && pid !== 'demo' && window.Echo) {
        channel = window.Echo.private(`config-changes.${pid}`).listen('.HostedDraftUpdated', handler)
      }
    },
    { immediate: true }
  )
  onScopeDispose(stop)
}
